import { X } from "lucide-react";
import { Spinner } from "../../../components/ui/spinner";
import ConnectionIcon from "./ConnectionIcon";
import StatusDot, { type LiveStatus } from "./StatusDot";
import type { MCPConnection } from "../types";
import { useTranslation } from "react-i18next";

export type { LiveStatus };

type Props = {
  connection: MCPConnection;
  status: LiveStatus;
  errorMessage?: string | null;
  isActive: boolean;
  isTogglingActive?: boolean;
  onToggleActive: () => void;
  onRefresh: () => void;
  isRefreshing?: boolean;
  onClose: () => void;
};

const DetailHeader = ({
  connection,
  status,
  errorMessage,
  isActive,
  isTogglingActive = false,
  onToggleActive,
  onRefresh,
  isRefreshing = false,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  return (
    <header className="flex items-center justify-between gap-3 px-6 py-4">
      <div className="flex items-center gap-3">
        <ConnectionIcon connection={connection} size="md" />
        <div className="flex items-center gap-2">
          <span className="text-base font-semibold text-foreground">
            {connection.name}
          </span>
          <span
            className="flex items-center gap-1.5 text-xs text-muted-foreground"
            title={errorMessage ?? undefined}
          >
            <StatusDot status={status} />
            {t(`mcpPage.status.${status}`)}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onToggleActive}
          disabled={isTogglingActive}
          aria-busy={isTogglingActive}
          className={`flex h-10 items-center gap-2 rounded-md px-3 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
            isActive
              ? "bg-destructive text-white hover:bg-destructive/90"
              : "border border-border bg-card text-foreground hover:bg-muted"
          }`}
        >
          {isTogglingActive && <Spinner className="size-3.5" />}
          {isActive ? t("mcpPage.actions.disable") : t("mcpPage.actions.enable")}
        </button>
        <button
          type="button"
          onClick={onRefresh}
          disabled={isRefreshing}
          aria-busy={isRefreshing}
          className="flex h-10 items-center gap-2 rounded-md border border-border bg-card px-3 text-sm text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isRefreshing && <Spinner className="size-3.5" />}
          {t("mcpPage.actions.refresh")}
        </button>
        {/*
          The panel used to be a MUI Drawer, which gave Esc and a click-away
          backdrop for free. Inline, it has neither — so this button is the only
          way out, not a nicety.
        */}
        <button
          type="button"
          onClick={onClose}
          aria-label={t("common.close")}
          className="flex size-10 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <X className="size-4" aria-hidden="true" />
        </button>
      </div>
    </header>
  );
};

export default DetailHeader;
